// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';
/**
 * LE TRI D'UNE LISTE DE CARTES (L4, 10/10/2026) — même moteur que `<Tableau>`.
 *
 * Quatre registres se lisent en LISTE (chaîne métrologique, parc des
 * équipements…), pas en tableau : leur fiche s'ouvre à côté, ou leurs lignes
 * portent des pastilles qui ne tiendraient pas dans une cellule. Ils ont
 * besoin d'un tri, et ce tri doit être CELUI DU TABLEAU : même cycle
 * (croissant → décroissant → défaut), même comparateur (nombres entre eux,
 * texte en français avec l'ordre numérique, vides en queue), mêmes glyphes.
 *
 * `SortableTh` faisait cela avec un `aria-sort` sur un `<button>` — attribut
 * inerte hors d'un en-tête de colonne (É-7). Ici, le bouton dit ce qu'il fait
 * dans son nom accessible (« Trier par échéance, croissant ») et `aria-pressed`
 * dit lequel est actif : ce qu'un lecteur d'écran sait annoncer.
 */
import React from 'react';
import { basculerTri, trierLignes, type EtatTri, type ValeurCellule } from './tableauMoteur';

export type { EtatTri } from './tableauMoteur';

export interface OptionTri { cle: string; libelle: string; aide?: string }

/** L'état de tri d'une liste, sa bascule et la liste triée — un seul moteur. */
export function useTri<L>(
  defaut: EtatTri | null,
  valeurs: Readonly<Record<string, (l: L) => ValeurCellule>>,
) {
  const [tri, setTri] = React.useState<EtatTri | null>(defaut);
  const basculer = React.useCallback((cle: string) => setTri((t) => basculerTri(t, cle, defaut)), [defaut]);
  const trier = React.useCallback((lignes: readonly L[]) => trierLignes(lignes, valeurs, tri), [valeurs, tri]);
  return { tri, basculer, trier };
}

/** Les boutons de tri d'une liste, en tête de carte. */
export function BoutonsTri({ label, options, tri, onTri, style }: {
  /** Ce que le groupe trie : « Trier la chaîne du parc ». */
  label: string;
  options: OptionTri[];
  tri: EtatTri | null;
  onTri: (cle: string) => void;
  style?: React.CSSProperties;
}) {
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap', ...style }}>
      {options.map((o) => {
        const actif = tri?.cle === o.cle;
        const sens = actif ? (tri!.sens === -1 ? 'décroissant' : 'croissant') : null;
        return (
          <button key={o.cle} type="button" onClick={() => onTri(o.cle)} className="ged-btn"
            aria-pressed={actif}
            aria-label={`Trier par ${o.libelle.toLowerCase()}${sens ? `, ${sens}` : ''}`}
            title={o.aide}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', padding: 0,
              cursor: 'pointer', font: 'inherit', fontFamily: 'var(--font-mono)', fontSize: 'var(--tableau-entete-police)',
              fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: actif ? 'var(--color-primary-active, var(--color-primary))' : 'var(--text-muted)',
            }}>
            {o.libelle}
            {/* Même règle que `<Tableau>` (SYS-3) : ⇅ dit « triable », les triangles disent « trié, et dans ce sens ». */}
            <span aria-hidden style={{ fontSize: actif ? '0.8em' : '0.9em', opacity: actif ? 1 : 0.5 }}>
              {actif ? (tri!.sens === -1 ? '▼' : '▲') : '⇅'}
            </span>
          </button>
        );
      })}
    </div>
  );
}
